import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FiUsers, FiUserPlus, FiEdit, FiTrash2, FiUpload, FiMail, FiAlertTriangle, FiRefreshCw, FiCheckCircle, FiKey, FiMoreVertical, FiChevronUp, FiChevronDown, FiCopy, FiCheck } from 'react-icons/fi';
import apiService from '../../services/api';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import BulkStudentsImportModal from './BulkStudentsImportModal';
import Notification from '../common/Notification';

const UserManagementContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    align-items: stretch;
    margin-bottom: 1.5rem;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const AddButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.875rem;
    gap: 0.5rem;
    justify-content: center;
    min-height: 48px;
    touch-action: manipulation;
  }
`;

const BulkImportButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.5rem;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.875rem;
    gap: 0.5rem;
    justify-content: center;
    margin-left: 0;
    min-height: 48px;
    touch-action: manipulation;
  }
`;

const RefreshButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.5rem;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.875rem;
    gap: 0.5rem;
    justify-content: center;
    margin-left: 0;
    min-height: 48px;
    touch-action: manipulation;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.5rem;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 1.5rem;
  }
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 1rem;
  }
`;

const FilterButtonContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const FilterButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: var(--background-tertiary);
    border-color: var(--primary-color);
  }
  
  &.active {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
    justify-content: center;
    width: 100%;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--text-primary);
  color: var(--background-primary);
  padding: 0.5rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 1000;
  margin-bottom: 0.5rem;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: var(--text-primary);
  }
  
  ${FilterButtonContainer}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const UsersTable = styled.div`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 2fr 1fr 1.5fr 1.5fr 1fr 80px;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background-color: var(--background-secondary);
  border-bottom: 1px solid var(--border-color);
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-primary);
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  padding: 0.25rem 0.5rem;
  margin: -0.25rem -0.5rem;
  border-radius: 4px;
  
  &:hover {
    color: var(--primary-color);
    background-color: var(--background-secondary);
  }
`;

const SortIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  color: ${props => props.active ? 'var(--primary-color)' : 'var(--text-muted)'};
  opacity: ${props => props.active ? 1 : 0.5};
  transition: all 0.2s ease;
  font-size: 16px;
  
  ${SortableHeader}:hover & {
    opacity: 1;
    color: var(--primary-color);
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 60px 2fr 1fr 1.5fr 1.5fr 1fr 80px;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    border-radius: 0;
    margin-bottom: 0.75rem;
    border: 1px solid var(--border-color);
    background: var(--background-primary);
    box-shadow: var(--shadow-sm);
    
    /* Hide role, phone, account created, status, and actions on mobile */
    > *:nth-child(3),
    > *:nth-child(4),
    > *:nth-child(5),
    > *:nth-child(6),
    > *:nth-child(7) {
      display: none;
    }
    
    
    /* Avatar styling */
    > *:first-child {
      flex-shrink: 0;
    }
    
    /* User info container */
    > *:nth-child(2) {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-weight: 600;
  font-size: 0.875rem;
  background-image: ${props => props.src ? `url(${props.src})` : 'none'};
  background-size: cover;
  background-position: center;
  
  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 1rem;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const UserEmailDesktop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const EmailText = styled.span`
  flex: 1;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 4px;
  opacity: 0.6;
  
  &:hover {
    color: var(--primary-color);
    opacity: 1;
    background-color: var(--background-secondary);
  }
  
  &.copied {
    color: var(--success-color);
    opacity: 1;
  }
`;

const UserName = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    font-weight: 600;
  }
`;

const UserEmail = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 0.8rem;
  }
`;

const UserRole = styled.span`
  color: var(--text-primary);
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 0.75rem;
  }
`;

const UserStatus = styled.span`
  color: var(--text-secondary);
  font-size: 0.75rem;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 0.75rem;
    color: ${props => props.isActive ? 'var(--success-color)' : 'var(--error-color)'};
  }
`;

const UserPhone = styled.span`
  color: var(--text-secondary);
  font-size: 0.75rem;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 0.75rem;
  }
`;

const UserCreatedDate = styled.span`
  color: var(--text-secondary);
  font-size: 0.75rem;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 0.75rem;
  }
`;

const ReminderLink = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 0.75rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    color: var(--primary-dark);
  }
  
  &:disabled {
    color: var(--text-muted);
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const MobilePasswordSection = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--border-color);
  }
`;

const DesktopOnly = styled.div`
  @media (max-width: 768px) {
    display: none !important;
  }
`;


const RoleBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  &.student {
    background-color: rgba(59, 130, 246, 0.1);
    color: var(--info-color);
  }
  
  &.instructor {
    background-color: rgba(147, 51, 234, 0.1);
    color: var(--accent-color);
  }
  
  &.admin {
    background-color: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
  }
`;


const ActionsDropdown = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropdownButton = styled.button`
  padding: 0.5rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-tertiary);
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.25rem;
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-md);
  z-index: 100;
  min-width: 160px;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  &.delete {
    color: var(--error-color);
    
    &:hover {
      background-color: rgba(252, 129, 129, 0.1);
    }
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: var(--text-primary);
  
  &.secondary {
    color: var(--text-secondary);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    font-size: 1rem;
    margin: 0;
  }
`;

const TabNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--border-color);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    gap: 0.25rem;
    min-width: 100%;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  font-weight: 500;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  flex-shrink: 0;
  
  &.active {
    color: var(--primary-color);
    border-bottom-color: var(--primary-color);
  }
  
  &:hover {
    color: var(--text-primary);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.75rem;
    font-size: 0.8rem;
    gap: 0.25rem;
    min-width: 0;
  }
`;

const LastLoginInfo = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

const PasswordStatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  
  &.default {
    background-color: rgba(246, 224, 94, 0.1);
    color: var(--warning-dark);
    border: 1px solid var(--warning-color);
  }
`;

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [defaultPasswordUsers, setDefaultPasswordUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [copiedEmailId, setCopiedEmailId] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const [usersResponse, defaultPasswordResponse] = await Promise.all([
        apiService.getUsers(),
        apiService.getUsersWithDefaultPasswords()
      ]);
      console.log('📊 Users fetched:', usersResponse.users?.length || 0);
      console.log('🔑 Default password users fetched:', defaultPasswordResponse.users?.length || 0);
      setUsers(usersResponse.users || []);
      setDefaultPasswordUsers(defaultPasswordResponse.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setDefaultPasswordUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let filtered = users.filter(user => {
      const matchesSearch = 
        (user.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilter = activeFilter === 'all' || user.role === activeFilter;
      
      return matchesSearch && matchesFilter;
    });

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortColumn) {
          case 'name':
            aValue = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
            bValue = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
            break;
          case 'email':
            aValue = (a.email || '').toLowerCase();
            bValue = (b.email || '').toLowerCase();
            break;
          case 'role':
            aValue = (a.role || '').toLowerCase();
            bValue = (b.role || '').toLowerCase();
            break;
          case 'phone':
            aValue = (a.phone || '').replace(/\D/g, '');
            bValue = (b.phone || '').replace(/\D/g, '');
            break;
          case 'created_at':
            aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
            bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
            break;
          case 'status':
            aValue = a.is_active ? 1 : 0;
            bValue = b.is_active ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [users, searchTerm, activeFilter, sortColumn, sortDirection]);

  const filterCounts = useMemo(() => {
    const allCount = users.length;
    const studentCount = users.filter(user => user.role === 'student').length;
    const instructorCount = users.filter(user => user.role === 'instructor').length;
    const adminCount = users.filter(user => user.role === 'admin').length;
    
    return { allCount, studentCount, instructorCount, adminCount };
  }, [users]);

  const getInitials = (firstName, lastName) => {
    const first = firstName || '';
    const last = lastName || '';
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleCopyEmail = async (email, userId) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmailId(userId);
      setTimeout(() => setCopiedEmailId(null), 2000);
    } catch (error) {
      console.error('Failed to copy email:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedEmailId(userId);
      setTimeout(() => setCopiedEmailId(null), 2000);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    // Remove all non-digit characters
    const phoneNumber = phone.replace(/\D/g, '');
    
    // Format as (xxx) xxx-xxxx if 10 digits
    if (phoneNumber.length === 10) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
    
    // Return as-is if not 10 digits
    return phone;
  };

  const handleAddUser = () => {
    setShowAddModal(true);
  };

  const handleBulkImport = () => {
    setShowBulkImportModal(true);
  };

  const handleSendPasswordReminder = async (user) => {
    try {
      setSendingEmail(true);
      const response = await apiService.sendPasswordReminder(user.id);
      
      if (response.success) {
        setNotification({
          isOpen: true,
          type: 'success',
          title: 'Email Sent',
          message: `Password reminder email sent to ${user.first_name} ${user.last_name}`
        });
      } else {
        throw new Error(response.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending password reminder:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to send password reminder email'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleUserAdded = (newUser) => {
    setUsers(prev => [newUser, ...prev]);
  };


  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleUserUpdated = (updatedUser) => {
    setUsers(prev => prev.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    ));
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = async () => {
    if (!selectedUser) return;
    
    setResetPasswordLoading(true);
    
    try {
      const response = await apiService.resetUserPassword(selectedUser.id, true);
      
      if (response.success) {
        // Close modal and reset state
        setShowResetPasswordModal(false);
        setSelectedUser(null);
        
        // Show success message
        if (response.data.passwordSent) {
          setNotification({
            isOpen: true,
            type: 'success',
            title: 'Password Reset',
            message: `Password has been reset for ${selectedUser.first_name} ${selectedUser.last_name}. An email with the new password has been sent.`
          });
        } else if (response.data.temporaryPassword) {
          setNotification({
            isOpen: true,
            type: 'success',
            title: 'Password Reset',
            message: `Password has been reset for ${selectedUser.first_name} ${selectedUser.last_name}. Temporary password: ${response.data.temporaryPassword}`
          });
        } else {
          setNotification({
            isOpen: true,
            type: 'success',
            title: 'Password Reset',
            message: `Password has been reset for ${selectedUser.first_name} ${selectedUser.last_name}.`
          });
        }
      } else {
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Reset Failed',
          message: response.error || 'Failed to reset password. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Reset Failed',
        message: error.message || 'Failed to reset password. Please try again.'
      });
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;
    
    setDeleteLoading(true);
    
    try {
      // Call the API to delete user
      await apiService.deleteUser(selectedUser.id);
      
      // Remove user from the list
      setUsers(prev => prev.filter(user => user.id !== selectedUser.id));
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setSelectedUser(null);
      
      // Show success message
      setNotification({
        isOpen: true,
        type: 'success',
        title: 'User Deleted',
        message: `User ${selectedUser.first_name} ${selectedUser.last_name} has been deleted successfully.`
      });
      
    } catch (error) {
      console.error('Error deleting user:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Delete Failed',
        message: 'Failed to delete user. Please try again.'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <UserManagementContainer>
        <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <FiUsers size={32} />
          <p>Loading users...</p>
        </div>
      </UserManagementContainer>
    );
  }

  return (
    <UserManagementContainer>
      <Header>
        <Title>User Management</Title>
        <ButtonGroup>
          <AddButton onClick={handleAddUser}>
            <FiUserPlus size={16} />
            Add User
          </AddButton>
          <BulkImportButton onClick={handleBulkImport}>
            <FiUpload size={16} />
            Bulk Students Import
          </BulkImportButton>
          <RefreshButton onClick={fetchUsers}>
            <FiRefreshCw size={16} />
            Refresh
          </RefreshButton>
        </ButtonGroup>
      </Header>
      <TabNavigation>
        <Tab 
          className={activeTab === 'all' ? 'active' : ''}
          onClick={() => setActiveTab('all')}
        >
          <FiUsers size={16} />
          All Users ({users.length})
        </Tab>
        <Tab 
          className={activeTab === 'default-passwords' ? 'active' : ''}
          onClick={() => setActiveTab('default-passwords')}
        >
          <FiAlertTriangle size={16} />
          Password Management ({defaultPasswordUsers.length})
        </Tab>
      </TabNavigation>
      <SearchBar>
        <SearchInput
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchBar>
      <Filters>
        <FilterButtonContainer>
          <FilterButton 
            className={activeFilter === 'all' ? 'active' : ''}
            onClick={() => setActiveFilter('all')}
          >
            All Users
          </FilterButton>
          <Tooltip>Total: {filterCounts.allCount} users</Tooltip>
        </FilterButtonContainer>
        
        <FilterButtonContainer>
          <FilterButton 
            className={activeFilter === 'student' ? 'active' : ''}
            onClick={() => setActiveFilter('student')}
          >
            Students
          </FilterButton>
          <Tooltip>{filterCounts.studentCount} students</Tooltip>
        </FilterButtonContainer>
        
        <FilterButtonContainer>
          <FilterButton 
            className={activeFilter === 'instructor' ? 'active' : ''}
            onClick={() => setActiveFilter('instructor')}
          >
            Instructors
          </FilterButton>
          <Tooltip>{filterCounts.instructorCount} instructors</Tooltip>
        </FilterButtonContainer>
        
        <FilterButtonContainer>
          <FilterButton 
            className={activeFilter === 'admin' ? 'active' : ''}
            onClick={() => setActiveFilter('admin')}
          >
            Admins
          </FilterButton>
          <Tooltip>{filterCounts.adminCount} admins</Tooltip>
        </FilterButtonContainer>
      </Filters>
      {(activeTab === 'all' ? filteredUsers : defaultPasswordUsers).length > 0 ? (
        <UsersTable>
          <TableHeader>
            <div>Avatar</div>
            <SortableHeader onClick={() => handleSort('name')}>
              Name
              <SortIcon active={sortColumn === 'name'}>
                {sortColumn === 'name' && sortDirection === 'asc' ? (
                  <FiChevronUp size={16} />
                ) : sortColumn === 'name' && sortDirection === 'desc' ? (
                  <FiChevronDown size={16} />
                ) : (
                  <FiChevronUp size={16} />
                )}
              </SortIcon>
            </SortableHeader>
            <SortableHeader onClick={() => handleSort('role')}>
              Role
              <SortIcon active={sortColumn === 'role'}>
                {sortColumn === 'role' && sortDirection === 'asc' ? (
                  <FiChevronUp size={16} />
                ) : sortColumn === 'role' && sortDirection === 'desc' ? (
                  <FiChevronDown size={16} />
                ) : (
                  <FiChevronUp size={16} />
                )}
              </SortIcon>
            </SortableHeader>
            <SortableHeader onClick={() => handleSort('phone')}>
              Phone
              <SortIcon active={sortColumn === 'phone'}>
                {sortColumn === 'phone' && sortDirection === 'asc' ? (
                  <FiChevronUp size={16} />
                ) : sortColumn === 'phone' && sortDirection === 'desc' ? (
                  <FiChevronDown size={16} />
                ) : (
                  <FiChevronUp size={16} />
                )}
              </SortIcon>
            </SortableHeader>
            <SortableHeader onClick={() => handleSort('created_at')}>
              Account Created
              <SortIcon active={sortColumn === 'created_at'}>
                {sortColumn === 'created_at' && sortDirection === 'asc' ? (
                  <FiChevronUp size={16} />
                ) : sortColumn === 'created_at' && sortDirection === 'desc' ? (
                  <FiChevronDown size={16} />
                ) : (
                  <FiChevronUp size={16} />
                )}
              </SortIcon>
            </SortableHeader>
            <SortableHeader onClick={() => handleSort('status')}>
              {activeTab === 'default-passwords' ? 'Password Status' : 'Status'}
              <SortIcon active={sortColumn === 'status'}>
                {sortColumn === 'status' && sortDirection === 'asc' ? (
                  <FiChevronUp size={16} />
                ) : sortColumn === 'status' && sortDirection === 'desc' ? (
                  <FiChevronDown size={16} />
                ) : (
                  <FiChevronUp size={16} />
                )}
              </SortIcon>
            </SortableHeader>
            <div>{activeTab === 'default-passwords' ? 'Last Login' : 'Actions'}</div>
          </TableHeader>
          
          {(activeTab === 'all' ? filteredUsers : defaultPasswordUsers).map((user, index) => (
            <TableRow
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0 }}
            >
              <Avatar src={user.avatar_url}>
                {!user.avatar_url && getInitials(user.first_name, user.last_name)}
              </Avatar>
              
              <UserInfo>
                <UserName>{user.first_name} {user.last_name}</UserName>
                <UserEmailDesktop>
                  <EmailText>{user.email}</EmailText>
                  <CopyButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyEmail(user.email, user.id);
                    }}
                    className={copiedEmailId === user.id ? 'copied' : ''}
                    title="Copy email to clipboard"
                  >
                    {copiedEmailId === user.id ? (
                      <FiCheck size={14} />
                    ) : (
                      <FiCopy size={14} />
                    )}
                  </CopyButton>
                </UserEmailDesktop>
                <UserEmail>{user.email}</UserEmail>
                <UserRole>{user.role}</UserRole>
                <UserStatus isActive={user.is_active}>{user.is_active ? 'active' : 'inactive'}</UserStatus>
                <UserPhone>Phone: {formatPhoneNumber(user.phone)}</UserPhone>
                <UserCreatedDate>
                  Created: {user.created_at 
                    ? DateTime.fromISO(user.created_at).toLocaleString(DateTime.DATE_MED)
                    : 'N/A'
                  }
                </UserCreatedDate>
              </UserInfo>
              
              <TableCell>
                <RoleBadge className={user.role}>
                  {user.role}
                </RoleBadge>
              </TableCell>
              
              <TableCell className="secondary">
                {formatPhoneNumber(user.phone)}
              </TableCell>
              
              <TableCell className="secondary">
                {user.created_at 
                  ? DateTime.fromISO(user.created_at).toLocaleString(DateTime.DATE_MED)
                  : 'N/A'
                }
              </TableCell>
              
              <TableCell>
                {activeTab === 'default-passwords' ? (
                  user.has_default_password ? (
                    <PasswordStatusBadge className="default">
                      <FiAlertTriangle size={12} /> Change Required
                    </PasswordStatusBadge>
                  ) : (
                    <PasswordStatusBadge style={{ backgroundColor: 'rgba(104, 211, 145, 0.1)', color: 'var(--success-dark)', border: '1px solid var(--success-color)' }}>
                      <FiCheckCircle size={12} /> Password Changed
                    </PasswordStatusBadge>
                  )
                ) : (
                  <span style={{ 
                    color: user.is_active ? 'var(--success-color)' : 'var(--error-color)',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}>
                    {user.is_active ? 'active' : 'inactive'}
                  </span>
                )}
              </TableCell>
              
              {activeTab === 'default-passwords' ? (
                <div style={{ 
                  display: 'block', 
                  width: '100%', 
                  overflow: 'hidden',
                  isolation: 'isolate'
                }}>
                  <DesktopOnly style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    width: '100%',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <LastLoginInfo style={{ 
                      display: 'block',
                      width: '100%',
                      margin: 0,
                      padding: 0
                    }}>
                      {user.last_login_at 
                        ? `Last login: ${DateTime.fromISO(user.last_login_at).toLocaleString(DateTime.DATETIME_MED)}`
                        : 'Not Available'
                      }
                    </LastLoginInfo>
                    {user.has_default_password && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <ReminderLink 
                          onClick={() => handleSendPasswordReminder(user)}
                          disabled={sendingEmail}
                          title="Send Password Reminder Email"
                        >
                          <FiMail size={12} />
                          Send Reminder
                        </ReminderLink>
                      </div>
                    )}
                  </DesktopOnly>
                  <MobilePasswordSection>
                    <LastLoginInfo>
                      {user.last_login_at 
                        ? `Last login: ${DateTime.fromISO(user.last_login_at).toLocaleString(DateTime.DATETIME_MED)}`
                        : 'Not Available'
                      }
                    </LastLoginInfo>
                    {user.has_default_password && (
                      <ReminderLink 
                        onClick={() => handleSendPasswordReminder(user)}
                        disabled={sendingEmail}
                        title="Send Password Reminder Email"
                      >
                        <FiMail size={12} />
                        Send Reminder
                      </ReminderLink>
                    )}
                  </MobilePasswordSection>
                </div>
              ) : (
                <TableCell>
                  <ActionsDropdown>
                    <DropdownButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdownId(openDropdownId === user.id ? null : user.id);
                      }}
                    >
                      <FiMoreVertical size={18} />
                    </DropdownButton>
                    <AnimatePresence>
                      {openDropdownId === user.id && (
                        <>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 99
                            }}
                            onClick={() => setOpenDropdownId(null)}
                          />
                          <DropdownMenu
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <DropdownItem onClick={() => {
                              handleEditUser(user);
                              setOpenDropdownId(null);
                            }}>
                              <FiEdit size={16} />
                              Edit User
                            </DropdownItem>
                            <DropdownItem onClick={() => {
                              handleResetPassword(user);
                              setOpenDropdownId(null);
                            }}>
                              <FiKey size={16} />
                              Reset Password
                            </DropdownItem>
                            <DropdownItem 
                              className="delete"
                              onClick={() => {
                                handleDeleteUser(user);
                                setOpenDropdownId(null);
                              }}
                            >
                              <FiTrash2 size={16} />
                              Delete User
                            </DropdownItem>
                          </DropdownMenu>
                        </>
                      )}
                    </AnimatePresence>
                  </ActionsDropdown>
                </TableCell>
              )}
            </TableRow>
          ))}
        </UsersTable>
      ) : (
        <EmptyState>
          <FiUsers />
          <h3>
            {activeTab === 'default-passwords' ? 'No password management needed' : 'No users found'}
          </h3>
          <p>
            {activeTab === 'default-passwords' 
              ? 'All users have secure passwords and no password management is needed.'
              : searchTerm 
                ? `No users match "${searchTerm}"`
                : 'There are no users in the system yet.'
            }
          </p>
        </EmptyState>
      )}
      <AddUserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onUserAdded={handleUserAdded}
      />
      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onUserUpdated={handleUserUpdated}
      />
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={confirmDeleteUser}
        item={selectedUser}
        itemType="user"
        loading={deleteLoading}
      />
      <DeleteConfirmationModal
        isOpen={showResetPasswordModal}
        onClose={() => {
          setShowResetPasswordModal(false);
          setSelectedUser(null);
        }}
        onConfirm={confirmResetPassword}
        item={selectedUser}
        itemType="user password"
        loading={resetPasswordLoading}
        title="Reset Password"
        message={selectedUser ? `Are you sure you want to reset the password for ${selectedUser.first_name} ${selectedUser.last_name}?` : ''}
        confirmButtonText="Reset Password"
      />
      <BulkStudentsImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
      />
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </UserManagementContainer>
  );
}

export default UserManagement;
